import { listAgencyUsers } from './agencyActions'
import { remoteRequest } from './httpClient'
import { readSession } from './session'
import { apiItems, normalizeScript } from './normalizers'

export async function scriptsCatalog() {
  try {
    const data = await remoteRequest('GET', '/customScripts/user/list')
    return apiItems(data, ['scripts', 'customScripts']).map(normalizeScript).filter((script) => script.uuid && script.url)
  } catch (_) {
    const data = await remoteRequest('GET', '/customScripts')
    return apiItems(data, ['scripts', 'customScripts']).map(normalizeScript).filter((script) => script.uuid && script.url)
  }
}

export async function userScripts(params) {
  const data = await remoteRequest('GET', '/customScripts/user', {
    query: {
      username: params.username,
      pcc: String(params.pcc || '').toUpperCase(),
      ppcc: String(params.parent_pcc || '').toUpperCase(),
    },
  })
  const scripts = apiItems(data, ['scripts', 'customScripts']).map(normalizeScript).filter((script) => script.uuid)

  try {
    const pccAssignedScripts = await pccScripts(params)
    const pccStatusByUuid = new Map(pccAssignedScripts.map((script) => [script.uuid, script]))
    return scripts.map((script) => {
      if (Number(script.foundinpcc || 0) !== 1) return script
      const inherited = pccStatusByUuid.get(script.uuid)
      if (!inherited) return script
      return {
        ...script,
        status: inherited.status,
        apply_config: inherited.apply_config,
      }
    })
  } catch (_) {
    return scripts
  }
}

export async function pccScripts(params) {
  const parentPcc = String(params.parent_pcc || '').toUpperCase()
  const data = await remoteRequest('GET', '/customScripts/pcc', {
    query: { ppcc: parentPcc, pcc: String(params.pcc || '').toUpperCase() },
    headers: { pcc: parentPcc },
  })
  return apiItems(data, ['scripts', 'customScripts']).map(normalizeScript).filter((script) => script.uuid)
}

const DEFAULT_SCRIPT_CONFIG = '{"title":"","messageinitial":"","validateActiveReservation":false,"sendCommands":false,"reintentos":0,"forms":[]}'

export async function listScriptCatalog() {
  try {
    const data = await remoteRequest('GET', '/customScripts/user/list')
    return apiItems(data, ['scripts', 'customScripts']).map(normalizeScript).filter((script) => script.uuid)
  } catch (_) {
    const data = await remoteRequest('GET', '/customScripts')
    return apiItems(data, ['scripts', 'customScripts']).map(normalizeScript).filter((script) => script.uuid)
  }
}

export async function createScript(body) {
  return remoteRequest('GET', '/customScripts/createCustomscript', {
    jwt: false,
    query: {
      name: body.name,
      status: String(body.status || '1'),
      uuid: '',
      description: body.description || '',
      url: body.url,
      config: body.config || DEFAULT_SCRIPT_CONFIG,
    },
  })
}

export async function updateScript(body) {
  return remoteRequest('GET', '/customScripts/updateCustomscript', {
    query: {
      uuid: String(body.uuid || '').toLowerCase(),
      name: body.name,
      description: body.description || '',
      url: body.url,
      status: String(body.status ?? '1'),
      keywords: body.keywords || '',
    },
  })
}

// Guarda la configuracion base del script (forms/remarks). El unico endpoint que
// acepta `config` es createCustomscript, que hace upsert cuando se le pasa el uuid.
export async function saveScriptConfig(body) {
  return remoteRequest('GET', '/customScripts/createCustomscript', {
    jwt: false,
    query: {
      name: body.name,
      status: String(body.status ?? '1'),
      uuid: String(body.uuid || '').toLowerCase(),
      description: body.description || '',
      url: body.url,
      config: body.config || '{}',
    },
  })
}

export async function getScriptConfig(params) {
  const session = readSession()
  const isPcc = params.kind === 'pcc'
  const endpoint = isPcc ? '/scripts/getConfigScriptPCC' : '/scripts/getConfigScriptUser'
  const query = isPcc
    ? {
      uuid: String(params.uuid || '').toLowerCase(),
      ppcc: String(params.parent_pcc || '').toUpperCase(),
      pcc: String(params.pcc || '').toUpperCase(),
    }
    : { uuid: String(params.uuid || '').toLowerCase(), usr: params.username }

  // El header `token` en este endpoint es el JWT (igual que Authorization), NO el token SABRE.
  const data = await remoteRequest('GET', endpoint, {
    token: session.jwt,
    query,
  })

  let config
  if (typeof data === 'string') {
    config = data
  } else if (data && typeof data === 'object' && typeof data.config === 'string') {
    config = data.config
  } else {
    // La respuesta suele ser el objeto de configuracion directamente (title, forms, ...)
    config = JSON.stringify(data ?? {}, null, 2)
  }

  return {
    uuid: String(params.uuid || '').toLowerCase(),
    name: String(data?.title || data?.name || ''),
    config,
  }
}

export async function assignScript(body) {
  return remoteRequest('GET', '/customScripts/createUserCustomScript', {
    query: {
      username: body.username,
      parent_pcc: String(body.parent_pcc || '').toUpperCase(),
      pcc: String(body.pcc || '').toUpperCase(),
      status: '1',
      uuid: String(body.uuid || '').toLowerCase(),
      config: body.config || '',
      applyconfig: body.apply_config || '0',
    },
  })
}

export async function assignScriptPcc(body) {
  return remoteRequest('GET', '/customScripts/createPCCCustomScript', {
    query: {
      parent_pcc: String(body.parent_pcc || '').toUpperCase(),
      pcc: String(body.pcc || '').toUpperCase(),
      status: '1',
      uuid: String(body.uuid || '').toLowerCase(),
      created_user: readSession().email || 'generic0',
      config: body.config || '',
      applyconfig: body.apply_config || '0',
    },
  })
}

export async function saveUserScriptAssignmentConfig(body) {
  return remoteRequest('POST', '/customScripts/updateConfigCustomscriptUser2', {
    body: {
      uuid: String(body.uuid || '').toLowerCase(),
      username: body.username,
      pcc: String(body.pcc || '').toUpperCase(),
      config: body.config || '{}',
      status: Number(body.status ?? 1),
      name: body.name || body.namescript || '',
    },
  })
}

export async function savePccScriptAssignmentConfig(body) {
  return remoteRequest('POST', '/customScripts/updateConfigCustomscriptPCC', {
    body: {
      uuid: String(body.uuid || '').toLowerCase(),
      ppcc: String(body.parent_pcc || body.ppcc || '').toUpperCase(),
      pcc: String(body.pcc || '').toUpperCase(),
      config: body.config || '{}',
      status: Number(body.status ?? 1),
      name: body.name || body.namescript || '',
      updated_user: body.updated_user || '',
    },
  })
}

export async function unassignScript(body) {
  return remoteRequest('GET', '/customScripts/deleteCustomscriptUser', {
    query: {
      uuid: String(body.uuid || '').toLowerCase(),
      pcc: String(body.pcc || '').toUpperCase(),
      username: body.username,
    },
  })
}

export async function unassignScriptPcc(body) {
  return remoteRequest('GET', '/customScripts/deleteCustomscriptPCC', {
    query: {
      uuid: String(body.uuid || '').toLowerCase(),
      pcc: String(body.pcc || '').toUpperCase(),
      ppcc: String(body.parent_pcc || '').toUpperCase(),
    },
  })
}

export async function toggleScriptStatus(body) {
  return remoteRequest('GET', '/customScripts/statusCustomscriptUser', {
    query: {
      uuid: String(body.uuid || '').toLowerCase(),
      pcc: String(body.pcc || '').toUpperCase(),
      username: body.username,
    },
  })
}

export async function toggleScriptStatusPcc(body) {
  return remoteRequest('GET', '/customScripts/statusCustomscriptPCC', {
    query: {
      uuid: String(body.uuid || '').toLowerCase(),
      pcc: String(body.pcc || '').toUpperCase(),
    },
  })
}

export async function bulkAssign(body) {
  const users = await listAgencyUsers({ parent_pcc: body.parent_pcc })
  const allPccs = body.all_pccs === '1'
  const targets = users.filter((user) => allPccs || !body.pcc || user.pcc === String(body.pcc).toUpperCase())
  const results = await Promise.allSettled(targets.map((user) => assignScript({
    ...body,
    username: user.username,
    parent_pcc: user.parent_pcc,
    pcc: user.pcc,
  })))
  return {
    ok: true,
    total: targets.length,
    succeeded: results.filter((result) => result.status === 'fulfilled').length,
    failed: results.filter((result) => result.status === 'rejected').length,
  }
}
